/**
 * Created by rathompson on 1/11/22
 * File: ActivationForm
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

// import statement lib, local then scss or css
import { useEffect, useMemo, useState } from 'react'
import { Body, noop } from '@panwds/react-ui'
import { Form, Textarea } from '@panwds/react-form-legacy'
import Collapse from '../../../../../common/components/collapse/Collapse'
import { AVAILABLE_ADD_ONS } from '../../../../constants/LabelConstants'
import { PRODUCT_PARAMETERS } from '../../../constants/SubscriptionConstant'
import LicenseCardHeader from '../../../LicenseCard/LicenseCardHeader'
import AddonListings from '../../../licenseProducts/productListings/AddonListings'
import BackboneListing from '../../../licenseProducts/productListings/sections/BackboneListing'
import ActivationDetails from './ActivationDetails'
import Terms from '../Terms'
import Addons from './Addons'
import CloudManagedToggle from './CloudManagedToggle'
import DividerLine from '../../../../../common/components/dividerLine/DividerLine'
import { getVarValue } from '../../../../../common/utils/Sparky'
import {
  APP_ID_DSS_ID,
  APP_ID_LOGGING_SERVICE, APP_ID_PRISMA_ACCESS,
  BANNER_MESSAGES,
  CONNECTED_PLATFORMS,
  CTX_CDL,
  NON_PANORAMA_APPS
} from '../../../../../common/constants/GlobalConstants'
import { FF_ENABLE_BACKBONE, FF_STANDALONE_CDL } from '../../../../../common/utils/FeatureFlags'
import AppBanner from './AppBanner'
import ConnectedPlatforms from './ConnectedPlatforms'
import { filterWithRestrictedAccounts, isNonemptyArray } from '../../../../../common/utils/Utils'

const prismaDescriptionEnabled = getVarValue('show_description')

const ActivationForm = ({
  onSubmit,
  onCancel,
  showForm,
  license,
  licenses,
  licenseContext,
  supportedRegionsByKey,
  productDetails,
  tenants,
  cspDropDownData,
  loading,
  domainPostFix,
  apps,
  onTSGChange,
  selectedTSGInstances,
  setError,
  isPanoramaLocked,
  selectedTreeNode,
  isAmend,
  setPanorama,
  validationReTrigger,
  isBackBoneInstanceAvailable = false,
  ...rest
}) => {
  const [filteredTenant, setFilteredTenant] = useState([])
  const [filteredCSP, setFilteredCSP] = useState([])
  const [isCloudPlatform, setIsCloudPlatform] = useState()
  const lockedTSG = useMemo(() => {
    return license.locked_tsg ?? license.tsg_id
  }, [license.locked_tsg, license.tsg_id])
  const lockedCSP = useMemo(() => {
    return license.support_account_id
  }, [license.support_account_id])

  const isStandaloneCDL = getVarValue(FF_STANDALONE_CDL, false) && licenseContext === CTX_CDL
  const isBackboneEnabled = getVarValue(FF_ENABLE_BACKBONE, false)

  useEffect(() => {
    if (lockedTSG) {
      setFilteredTenant(tenants?.filter(each => each.id === lockedTSG))
    }
    else {
      setFilteredTenant(tenants)
    }
    if (lockedCSP) {
      setFilteredCSP(cspDropDownData?.filter(each => +each.id === +lockedCSP))
    }
    else (
      setFilteredCSP(cspDropDownData)
    )
    if (licenseContext === CTX_CDL) { //Restrict Standalone SLS onboarding only for BuckBeak beta users
      const betaAccounts = apps[APP_ID_LOGGING_SERVICE]?.extra?.buckbeak_beta_accounts
      setFilteredCSP(isNonemptyArray(betaAccounts) ? filterWithRestrictedAccounts(cspDropDownData, betaAccounts) : [])
    }
  }, [lockedTSG, lockedCSP, tenants, cspDropDownData, licenseContext, apps])


  const cloudEnabled = useMemo(() => {
    if (!license) {
      return false
    }
    // if (license?.entitlements?.length) {
    //   setPanChina(license?.entitlements.some(({ app_id }) => app_id === APP_ID_PAN_CHINA))
    // }
    return license?.app_id !== 'cgx'
  }, [license])


  const bannerDetails = BANNER_MESSAGES[licenseContext] || false
  const isNonPanoramaApp = !NON_PANORAMA_APPS.has(license?.app_id)

  const connectedApps = useMemo(() => {
    const connectedApps = CONNECTED_PLATFORMS[license?.app_id]
    if (connectedApps) {
      return (<ConnectedPlatforms {...{
        licenseContext,
        selectedTSGInstances
      }}
      />)
    }
  }, [license?.app_id, licenseContext, selectedTSGInstances])
  return (
    <Form onSubmit={onSubmit} mode="onBlur" formProps={{ autoComplete: 'off' }}>
      <Collapse isOpen={showForm}>
        <ActivationDetails {...{
          license,
          licenses,
          licenseContext,
          loading,
          setPanorama,
          tenants: filteredTenant,
          cspDropDownData: filteredCSP,
          apps,
          onTSGChange,
          lockedTSG,
          lockedCSP,
          domainPostFix,
          setError,
          ...rest,
        }} />
      </Collapse>
      {isStandaloneCDL ?
        <Collapse isOpen={showForm}>
          <Terms {...{ onCancel, loading, licenseContext, selectedTSGInstances, rest }} />
        </Collapse> : (
          <>
            <div className="tw-p-3">
              <LicenseCardHeader {...productDetails} license={license} showImage={showForm} />
            </div>
            <Collapse isOpen={showForm}>
              {connectedApps}
              {isNonPanoramaApp && <div className="tw-p-3">
                {cloudEnabled && <CloudManagedToggle
                  {...{ loading,
                    cloudEnabled,
                    license,
                    licenses,
                    setError,
                    isPanoramaLocked,
                    selectedTSGInstances,
                    setIsCloudPlatform,
                    isAmend }} />}
                {prismaDescriptionEnabled && (
                  <div>
                    <Body size="lg" addClassName="tw-font-lato tw-italic tw-mb-3">Description</Body>
                    <Textarea
                      name={'description'}
                      label={'description'}
                      showLabel={false}
                      rowsMin={2}
                      rowsMax={5}
                      placeholder={'Placeholder...'}
                      disabled={loading}
                      register={{
                        required: false
                      }}
                    />
                    <DividerLine />
                  </div>
                )}
                {/* ADD ONS */}
                <Body size="lg" addClassName="tw-font-lato tw-italic tw-mb-3">{AVAILABLE_ADD_ONS}</Body>
                <Addons {...{
                  license,
                  cspDropDownData: filteredCSP,
                  loading,
                  domainPostFix,
                  apps,
                  licenseContext,
                  selectedTSGInstances,
                  selectedTreeNode
                }}
                />
                { licenseContext === APP_ID_PRISMA_ACCESS && <AddonListings
                  {...{
                    //...props,
                    itemList: [{
                      'app_id': APP_ID_DSS_ID,
                      'product_type': 'PA-ADD',
                      'license_type': 'PA-ADD',
                      'purchased_size': 1,
                      'display': true,
                      'checkBox': 'isDSSRequired',
                      'formFieldName': 'directory_sync:formFiled'
                    }],
                    indexKey: APP_ID_DSS_ID,
                    selectedTreeNode,
                    instancesKeyBy: {},
                    isSingleTenant: true,
                    ...PRODUCT_PARAMETERS['directorySync'],
                  }}
                />}
                {isBackboneEnabled && isCloudPlatform && isBackBoneInstanceAvailable && <BackboneListing {...{ rest, isSingleTenant: true, apps }}/>}
              </div>
              }
              {bannerDetails && <AppBanner {...bannerDetails}></AppBanner>}
              {/* TermsAndConditions and Conditions */}
              <Terms {...{ onCancel, loading, licenseContext, selectedTSGInstances, validationReTrigger, rest }} />
            </Collapse>
          </>
        )}
    </Form>
  )
}

ActivationForm.propTypes = {}

ActivationForm.defaultProps = {
  onSubmit: noop,
  onCancel: noop,
  showForm: false,
  license: {},
  licenses: [],
  licenseContext: '',
  supportedRegionsByKey: {},
  productDetails: {},
  tenants: [],
  cspDropDownData: [],
  loading: false,
  domainPostFix: {},
  apps: {},
  onTSGChange: noop,
  selectedTSGInstances: [],
}

export default ActivationForm
